import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import type { LeadCardProviderProps } from '@app/components/LeadCard/context';
import {
  LEAD_CARD_DEFAULT_VIEW_CONFIG,
  type LeadCardBranchItemData,
} from '@app/components/LeadCard/data';
import {
  useLeadMessageHistory,
  useLeadMessageHistoryEffects,
} from '@app/components/LeadCard/ui/MessageHistory/hooks';
import { useWindowSize } from '@app/hooks';

import { useLeadData } from './useLeadData';

// Coalesce bursts of realtime history events into a single lead-card refetch so
// fresh lead state (e.g. IsNoAnswer / UserAnswerTill for the answer timer)
// arrives without a request per incoming message.
const LEAD_CARD_REFETCH_DEBOUNCE_MS = 600;

export interface UseBaseStateParams {
  leadId: LeadCardProviderProps['leadId'];
  className?: LeadCardProviderProps['className'];
  showLeadList?: LeadCardProviderProps['showLeadList'];
  showGoToLeadAction?: LeadCardProviderProps['showGoToLeadAction'];
  knownClientId?: LeadCardProviderProps['knownClientId'];
  leads?: LeadCardProviderProps['leads'];
  hasNextLeadsPage?: LeadCardProviderProps['hasNextLeadsPage'];
  onBackToLeads?: LeadCardProviderProps['onBackToLeads'];
  onLoadNextLeads?: LeadCardProviderProps['onLoadNextLeads'];
  onLeadClick?: LeadCardProviderProps['onLeadClick'];
  onLeadUpdated?: LeadCardProviderProps['onLeadUpdated'];
  isLeadsLoading?: LeadCardProviderProps['isLeadsLoading'];
  isNextLeadsPageLoading?: LeadCardProviderProps['isNextLeadsPageLoading'];
  isBranchesLoading?: LeadCardProviderProps['isBranchesLoading'];
  isMessagesLoading?: LeadCardProviderProps['isMessagesLoading'];
  isFooterLoading?: LeadCardProviderProps['isFooterLoading'];
  isUserDetailsLoading?: LeadCardProviderProps['isUserDetailsLoading'];
  viewConfig?: LeadCardProviderProps['viewConfig'];
}

export const useBaseState = ({
  leadId,
  className,
  showLeadList = true,
  showGoToLeadAction = false,
  knownClientId = null,
  leads: leadsProp,
  hasNextLeadsPage = false,
  onBackToLeads,
  onLoadNextLeads,
  onLeadClick,
  onLeadUpdated,
  isLeadsLoading: isLeadsLoadingProp,
  isNextLeadsPageLoading = false,
  isBranchesLoading: isBranchesLoadingProp,
  isMessagesLoading: isMessagesLoadingProp,
  isFooterLoading: isFooterLoadingProp,
  isUserDetailsLoading: isUserDetailsLoadingProp,
  viewConfig,
}: UseBaseStateParams) => {
  const leads = useMemo(() => leadsProp ?? [], [leadsProp]);
  const isLeadsLoading = isLeadsLoadingProp ?? false;

  const numericLeadId = useMemo<number | null>(() => {
    if (typeof leadId === 'number') {
      return Number.isFinite(leadId) ? leadId : null;
    }

    const parsed = Number(leadId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [leadId]);

  const refreshLeadHistoryAfterResponsibleChangeRef = useRef<
    (() => Promise<void>) | null
  >(null);
  const refreshLeadHistoryAfterResponsibleChange = useCallback(async () => {
    await refreshLeadHistoryAfterResponsibleChangeRef.current?.();
  }, []);

  const leadListUnreadMessage = useMemo(
    () => leads.find(({ Id }) => Id === numericLeadId)?.UnreadMessage,
    [leads, numericLeadId]
  );

  const leadData = useLeadData({
    leadId: numericLeadId,
    leadListUnreadMessage,
    onLeadUpdated,
    onResponsibleChangeSuccess: refreshLeadHistoryAfterResponsibleChange,
  });

  const [isLeadListDrawerOpen, setIsLeadListDrawerOpen] = useState(false);
  const [isUserDetailsDrawerOpen, setIsUserDetailsDrawerOpen] = useState(false);

  const { innerWidth } = useWindowSize();

  // Колонки списка лидов и деталей показываются по ширине САМОЙ карточки, а
  // не окна: в модалке записи карточка занимает лишь часть экрана, и по
  // ширине окна выходило, что колонка деталей «есть», хотя грид с её
  // минимумом не помещался в контейнер и она уезжала за границу
  // (`overflow: hidden` у .LeadCard) — а кнопка «Открыть детали» при этом не
  // показывалась (issue/10978). Пока карточка не измерена, работает прежнее
  // правило по ширине окна.
  const [cardNode, setCardNode] = useState<HTMLElement | null>(null);
  const [cardWidth, setCardWidth] = useState(0);

  const cardRef = useCallback((node: HTMLElement | null) => {
    setCardNode(node);
  }, []);

  useEffect(() => {
    if (!cardNode) return undefined;

    const updateCardWidth = () => setCardWidth(cardNode.clientWidth);

    updateCardWidth();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateCardWidth);
    observer.observe(cardNode);

    return () => observer.disconnect();
  }, [cardNode]);

  const layoutWidth = cardWidth || innerWidth;

  const showLeadsColumn = useMemo(
    () => showLeadList && layoutWidth >= 1200,
    [showLeadList, layoutWidth]
  );
  const showDetailsColumn = useMemo(() => layoutWidth >= 1000, [layoutWidth]);

  const openLeadListDrawer = useCallback(() => {
    if (!showLeadList) return;

    setIsLeadListDrawerOpen(true);
  }, [showLeadList]);

  const closeLeadListDrawer = useCallback(() => {
    setIsLeadListDrawerOpen(false);
  }, []);

  const openUserDetailsDrawer = useCallback(() => {
    setIsUserDetailsDrawerOpen(true);
  }, []);

  const closeUserDetailsDrawer = useCallback(() => {
    setIsUserDetailsDrawerOpen(false);
  }, []);

  const {
    selectBranch: realSelectBranch,
    openAddBranchModal,
    closeAddBranchModal,
    refetchLeadCard,
    isAddBranchModalOpen,
    leadCardData,
    leadDetails,
    applyLeadUpdate,
    applyManualUnreadLeadUpdate,
    organizationComment,
    setOrganizationComment,
  } = leadData;

  const selectBranch = useCallback(
    (branchId: LeadCardBranchItemData['id']) => {
      realSelectBranch(branchId);
    },
    [realSelectBranch]
  );

  const openCreateBranchModal = useCallback(() => {
    openAddBranchModal();
  }, [openAddBranchModal]);

  const closeCreateBranchModal = useCallback(() => {
    closeAddBranchModal();
  }, [closeAddBranchModal]);

  const selectLead = useCallback(
    (lead: ModelLead) => {
      onLeadClick?.(lead);
    },
    [onLeadClick]
  );

  const resolvedSelectedLeadId = numericLeadId;
  const isLeadCardDataActual =
    resolvedSelectedLeadId !== null &&
    leadCardData?.LeadId === resolvedSelectedLeadId;
  const isLeadCardIdentityPending =
    leadData.isLeadCardLoading ||
    leadData.isLeadCardUninitialized ||
    (resolvedSelectedLeadId !== null && !isLeadCardDataActual);
  const isLeadCardDetailsPending =
    isLeadCardIdentityPending || leadData.isLeadCardFetching;

  const resolvedSelectedLead = useMemo(() => {
    if (resolvedSelectedLeadId === null) return null;

    return (
      leads.find(({ Id }) => Id === resolvedSelectedLeadId) ??
      (isLeadCardDataActual ? leadCardData?.Lead : null) ??
      null
    );
  }, [isLeadCardDataActual, leadCardData?.Lead, resolvedSelectedLeadId, leads]);

  const resolvedIsBranchesLoading =
    isBranchesLoadingProp ?? isLeadCardDetailsPending;
  // User details and footer keep showing existing data during a background
  // refetch (e.g. realtime history events) instead of flashing skeletons. They
  // only show skeletons while the card identity is pending: initial load,
  // uninitialized query, or a lead switch whose data is not yet current.
  const resolvedIsUserDetailsLoading =
    isUserDetailsLoadingProp ?? isLeadCardIdentityPending;
  const resolvedIsFooterLoading =
    isFooterLoadingProp ?? isLeadCardIdentityPending;

  const refetchLeadCardDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const scheduleLeadCardRefetch = useCallback(() => {
    if (refetchLeadCardDebounceRef.current !== null) {
      clearTimeout(refetchLeadCardDebounceRef.current);
    }

    refetchLeadCardDebounceRef.current = setTimeout(() => {
      refetchLeadCardDebounceRef.current = null;
      refetchLeadCard();
    }, LEAD_CARD_REFETCH_DEBOUNCE_MS);
  }, [refetchLeadCard]);

  useEffect(
    () => () => {
      if (refetchLeadCardDebounceRef.current !== null) {
        clearTimeout(refetchLeadCardDebounceRef.current);
        refetchLeadCardDebounceRef.current = null;
      }
    },
    [resolvedSelectedLeadId]
  );

  const leadMessageHistory = useLeadMessageHistory({
    leadCardData,
    selectedLeadId: resolvedSelectedLeadId,
    isLeadCardPending: isLeadCardIdentityPending,
    onRelevantClientHistory: scheduleLeadCardRefetch,
  });

  useEffect(() => {
    refreshLeadHistoryAfterResponsibleChangeRef.current =
      leadMessageHistory.refreshLatestHistory;

    return () => {
      refreshLeadHistoryAfterResponsibleChangeRef.current = null;
    };
  }, [leadMessageHistory.refreshLatestHistory]);

  useLeadMessageHistoryEffects({
    selectedLeadClientIds: leadMessageHistory.selectedLeadClientIds,
    hasPendingOutgoingMessages: leadMessageHistory.hasPendingOutgoingMessages,
    onClientHistoryUpdateHandler:
      leadMessageHistory.onClientHistoryUpdateHandler,
    refreshLatestHistory: leadMessageHistory.refreshLatestHistory,
  });

  const isMessagesLoading =
    isMessagesLoadingProp ?? leadMessageHistory.isLeadMessageHistoryLoading;

  const resolvedViewConfig = useMemo(
    () => ({
      ...LEAD_CARD_DEFAULT_VIEW_CONFIG,
      ...viewConfig,
    }),
    [viewConfig]
  );

  return useMemo(
    () => ({
      leadId,
      className,
      isLeadListEnabled: showLeadList,
      isGoToLeadActionEnabled: showGoToLeadAction,
      knownClientId,
      isLeadListDrawerOpen,
      isUserDetailsDrawerOpen,
      openLeadListDrawer,
      closeLeadListDrawer,
      openUserDetailsDrawer,
      closeUserDetailsDrawer,
      branches: leadData.branches,
      selectedBranchId: leadData.selectedBranchId,
      selectBranch,
      openCreateBranchModal,
      closeCreateBranchModal,
      isCreateBranchModalOpen: isAddBranchModalOpen,
      refetchLeadCard,
      leadCardData,
      leadDetails,
      applyLeadUpdate,
      applyManualUnreadLeadUpdate,
      leads,
      hasNextLeadsPage,
      onBackToLeads,
      onLoadNextLeads,
      isNextLeadsPageLoading,
      selectedLeadId: resolvedSelectedLeadId,
      selectedLead: resolvedSelectedLead,
      selectLead,
      selectedLeadUserDetails: leadDetails,
      selectedLeadHistoryItems: leadMessageHistory.selectedLeadHistoryItems,
      pinnedCommentIdsAheadOfHistory:
        leadMessageHistory.pinnedCommentIdsAheadOfHistory,
      selectedLeadClientIds: leadMessageHistory.selectedLeadClientIds,
      isLeadMessageHistoryInitialized:
        leadMessageHistory.isLeadMessageHistoryInitialized,
      isLeadMessageHistoryCurrent:
        leadMessageHistory.isLeadMessageHistoryCurrent,
      leadMessageHistoryError: leadMessageHistory.leadMessageHistoryError,
      refetchLeadHistory: leadMessageHistory.refetchLeadHistory,
      refreshLatestHistory: leadMessageHistory.refreshLatestHistory,
      removeHistoryItem: leadMessageHistory.removeHistoryItem,
      loadOlderHistory: leadMessageHistory.loadOlderHistory,
      loadAllHistory: leadMessageHistory.loadAllHistory,
      isOlderHistoryLoading: leadMessageHistory.isOlderHistoryLoading,
      isAllHistoryLoading: leadMessageHistory.isAllHistoryLoading,
      isHistoryFullyLoaded: leadMessageHistory.isHistoryFullyLoaded,
      canLoadOlderHistory: leadMessageHistory.canLoadOlderHistory,
      olderHistoryLoadFailed: leadMessageHistory.olderHistoryLoadFailed,
      scrollContainerRef: leadMessageHistory.scrollContainerRef,
      scrollToBottom: leadMessageHistory.scrollToBottom,
      ensureHistoryFitsViewport: leadMessageHistory.ensureHistoryFitsViewport,
      selectedLeadIntegrations: leadData.integrations,
      isLeadsLoading,
      isBranchesLoading: resolvedIsBranchesLoading,
      isMessagesLoading,
      isFooterLoading: resolvedIsFooterLoading,
      isUserDetailsLoading: resolvedIsUserDetailsLoading,
      viewConfig: resolvedViewConfig,
      cardRef,
      showLeadsColumn,
      showDetailsColumn,
      organizationComment,
      setOrganizationComment,
    }),
    [
      leadId,
      className,
      showLeadList,
      showGoToLeadAction,
      knownClientId,
      isLeadListDrawerOpen,
      isUserDetailsDrawerOpen,
      openLeadListDrawer,
      closeLeadListDrawer,
      openUserDetailsDrawer,
      closeUserDetailsDrawer,
      leadData.branches,
      leadData.selectedBranchId,
      leadData.integrations,
      selectBranch,
      openCreateBranchModal,
      closeCreateBranchModal,
      isAddBranchModalOpen,
      refetchLeadCard,
      leadCardData,
      leadDetails,
      applyLeadUpdate,
      applyManualUnreadLeadUpdate,
      leads,
      hasNextLeadsPage,
      onBackToLeads,
      onLoadNextLeads,
      isNextLeadsPageLoading,
      resolvedSelectedLeadId,
      resolvedSelectedLead,
      selectLead,
      leadMessageHistory.selectedLeadHistoryItems,
      leadMessageHistory.pinnedCommentIdsAheadOfHistory,
      leadMessageHistory.selectedLeadClientIds,
      leadMessageHistory.isLeadMessageHistoryInitialized,
      leadMessageHistory.isLeadMessageHistoryCurrent,
      leadMessageHistory.leadMessageHistoryError,
      leadMessageHistory.refetchLeadHistory,
      leadMessageHistory.refreshLatestHistory,
      leadMessageHistory.removeHistoryItem,
      leadMessageHistory.loadOlderHistory,
      leadMessageHistory.loadAllHistory,
      leadMessageHistory.isOlderHistoryLoading,
      leadMessageHistory.isAllHistoryLoading,
      leadMessageHistory.isHistoryFullyLoaded,
      leadMessageHistory.canLoadOlderHistory,
      leadMessageHistory.olderHistoryLoadFailed,
      leadMessageHistory.scrollContainerRef,
      leadMessageHistory.scrollToBottom,
      leadMessageHistory.ensureHistoryFitsViewport,
      isLeadsLoading,
      resolvedIsBranchesLoading,
      isMessagesLoading,
      resolvedIsFooterLoading,
      resolvedIsUserDetailsLoading,
      resolvedViewConfig,
      cardRef,
      showLeadsColumn,
      showDetailsColumn,
      organizationComment,
      setOrganizationComment,
    ]
  );
};

export type BaseState = ReturnType<typeof useBaseState>;
